
import React from 'react';
import { sprintf } from "sprintf-js";
import {Action, Spinner, Times } from "../../../client/funcs/utils";
import { FileChooser } from "../../../client/components/file-dialog";
import { UploadFile, DeleteUploadFile } from "../../../client/onboarding/actions";
import { get_div_bbox_elem } from "../../prm/bbox_utils";

const FILE_STATE = {
  NEW: "NEW",
  OPEN_DIALOG: "OPEN_DIALOG",
  FILE_SELECTED: "FILE_SELECTED",
  FILE_UPLOADING: "FILE_UPLOADING",
  FILE_DELETING: "FILE_DELETING",
  FILE_UPLOADED: "FILE_UPLOADED",
};

const ACTION_LABEL = {
  NEW: "Browse",
  OPEN_DIALOG: "Browse",
  FILE_SELECTED: "Upload",
  FILE_UPLOADING: (<Spinner/>),
  FILE_DELETING: (<Spinner/>),
  FILE_UPLOADED: "Uploaded",
}

const MAX_NUM_FILES = 100;

const state_prop = (prefix, index) => {
  return sprintf("%s_state_%d", prefix, index);
}

const path_prop = (prefix, index) => {
  return sprintf("%s_path_%d", prefix, index);
}

const UploadFiles = props => {

  const { prefix, component } = props;
  const files = [];
  const error_prop = prefix + "_error";
  const max_files = props.max_files || MAX_NUM_FILES;

  for(let i=0; i < max_files; i++) {
    const prop = state_prop(prefix, i);
    if (!(prop in component.state)) {
      break;
    }
    files.push(component.state[prop]);
    if (component.state[prop] === FILE_STATE.NEW) {
      break;
    }
  }

  const label_style = {
    fontWeight: "800",
  };
  const do_label = !!props.label;

  const on_click = (state, index) => () => {
    if (state === FILE_STATE.NEW) {
      component.setState({
        [state_prop(prefix, index)]: FILE_STATE.OPEN_DIALOG,
        [path_prop(prefix, index)]: "",
        highlight_rects: [],
        [error_prop]: "",
      });
      component.openFileDialog(prefix, index);
    } else if (state === FILE_STATE.FILE_SELECTED) {
      component.setState({
        [state_prop(prefix, index)]: FILE_STATE.FILE_UPLOADING,
        [error_prop]: "",
      });
      UploadFile(
        component.props.token,
        component.props.onboarding_token,
        component.getFileObject(prefix, index),
        prefix,
        component.getOnboardingIndex(),
        component.props.username)
        .then(result => {
          component.setState({
            [state_prop(prefix, index)]: FILE_STATE.FILE_UPLOADED,
          });
          component.props.dataToStore(
            result.onboarding, component.getOnboardingIndex(),
            component.props.username);
        })
    }
  };

  const on_file_selected = (index) => (file) => {
    component.setFileObject(prefix, index, file);

    if (file) {
      // File selected
      component.setState({
        [state_prop(prefix, index)]: FILE_STATE.FILE_SELECTED,
        [path_prop(prefix, index)]: file.name,
      })
    } else {
      // Cancel clicked
      component.setState({
        [state_prop(prefix, index)]: FILE_STATE.NEW,
      })
    }

  };

  const on_remove = (index) => () => {
    const state = component.state[state_prop(prefix, index)];
    if (state === FILE_STATE.FILE_SELECTED) {
      component.setState({
        [state_prop(prefix, index)]: FILE_STATE.NEW,
        [path_prop(prefix, index)]: "",
      })
    } else if (state === FILE_STATE.FILE_UPLOADED) {

      component.setState({
        [state_prop(prefix, index)]: FILE_STATE.FILE_DELETING,
      });

      DeleteUploadFile(
        component.props.token,
        component.props.onboarding_token,
        prefix,
        component.getOnboardingIndex(),
        index)
        .then(result => {
          component.setState({
            [state_prop(prefix, index)]: FILE_STATE.NEW,
            [path_prop(prefix, index)]: "",
          });
          component.props.dataToStore(result.onboarding, component.getOnboardingIndex());
        })
    }
  }

  const state_to_elem = (state, index) => {
    const action_label = ACTION_LABEL[state];
    const name_prop = path_prop(prefix, index);
    const value = (name_prop in component.state)
      ? component.state[name_prop] : "";
    const x_color = state === FILE_STATE.NEW ? "#BBB" : "#000";

    const action_style = {
      padding: "0.4rem 0.5rem",
      width: "90%",
    };
    const input_disabled = state === FILE_STATE.FILE_UPLOADED
      || state === FILE_STATE.FILE_DELETING;
    const show_input = state !== FILE_STATE.FILE_UPLOADED;
    return (
      <div style={{display: "flex", padding: "0.25rem 0"}} key={index}>
        { do_label &&  <div style={{flex: "0 0 8rem", alignSelf: "center"}}>
          <span style={label_style}>{sprintf(props.label, index)}</span>
        </div> }
        <div
          style={{flex: "0 0 auto", paddingRight: "1rem"}}
          onClick={on_remove(index)}>
          <Times style={{fontSize: "2rem", color: x_color}}/>
        </div>
        <div style={{flex: "1 0", alignSelf: "center"}}>
          { show_input && <input
            type={"text"}
            style={{width: "100%"}}
            value={value}
            ref={component.setElemRef("input", index)}
            disabled={input_disabled}
            readOnly={true} /> }
          { !show_input && <span style={{fontSize: "1.1rem"}}>{value}</span>}
        </div>
        <div style={{flex: "0 0 8rem", paddingLeft: "1rem"}}>
          <Action
            disabled={state === FILE_STATE.FILE_UPLOADED}
            style={action_style}
            onClick={on_click(state, index)}>
            { action_label }
          </Action>
        </div>
        <FileChooser
          ref={component.setElemRef(prefix, index)}
          onChange={on_file_selected((index))}/>
      </div>
    )
  }

  return (
    <div>
      { files.map(state_to_elem) }
    </div>
  )
};

const isFilledElseHighlight = (prefix, component, highlight_rects) => {
  // Verify only the last file is NEW. The rest need to be
  // uploaded.

  const error_prop = prefix + "_error";
  let error_str;
  const bad_index = [];
  for(let i=0; i < MAX_NUM_FILES; i++) {
    const prop = state_prop(prefix, i);
    if (component.state[prop] === FILE_STATE.NEW) {
      if (i === 0) {
        bad_index.push(i);
        error_str = "Please choose a file to upload";
      }
      break;
    }
    if (component.state[prop] !== FILE_STATE.FILE_UPLOADED) {
      bad_index.push(i);
      error_str = "Don't forget to click 'Upload'";
    }
  }

  if (error_str) {
    component.setState({
      [error_prop]: error_str,
    })
  }
  if (bad_index.length === 0) {
    // No bad items
    return true;
  }

  React.createRef();

  bad_index.forEach(index => {
    const bbox = get_div_bbox_elem(component.getElemRef("input", index));
    if (bbox) {
      highlight_rects.push(bbox);
    }
  })

};


const init_state = (prefix, onboarding, new_state) => {

  let total = 0;

  if (prefix in onboarding) {
    onboarding[prefix].forEach((file, index) => {
      new_state[state_prop(prefix, index)] = FILE_STATE.FILE_UPLOADED;
      new_state[path_prop(prefix, index)] = file.name;
    });
    total = onboarding[prefix].length;
  }

  new_state[state_prop(prefix, total)] = FILE_STATE.NEW;
  new_state[path_prop(prefix, total)] = "";
};

export { UploadFiles, init_state, isFilledElseHighlight };
